/**
 * Field Manager
 * Handles field polygons and markers display and interaction
 * Manages clustering based on zoom level
 */

import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FieldService, Field } from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { OpenStreetMapComponent } from '@components/openstreetmap/openstreetmap.component';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { PolygonConverterUtil } from '@app/utils/polygon-converter.util';
import { GisCircleMarker } from '@app/pages/electronic-field-map/pages/gis-fields/interfaces/gis-component.interfaces';
import { createRegionCircleMarkers } from '@app/utils/circle-marker.util';
import { RegionManagerService } from '@components/openstreetmap/managers/region-manager.service';
import { FieldsByRegionsResponse } from '@app/services/fields-by-regions.types';

@Injectable({ providedIn: 'root' })
export class FieldManager {
  private translate = inject(TranslateService);
  public fieldService = inject(FieldService);
  private mapInstance: OpenStreetMapComponent | null = null;
  private regionManager = inject(RegionManagerService);

  private lastNameFilterValue: string | null = null;

  COLOR_FIELD_COUNT = '#4CAF50';

  // Store data
  public fields$ = new BehaviorSubject<Field[]>([]);

  // Events
  public fieldMarkerClick$ = new Subject<{ lat: number; lng: number; data: any }>();
  public fieldPolygonClick$ = new Subject<any>();

  // Zoom settings
  private currentZoom: number = 13;
  private zoomThreshold: number = 14; // Show markers/polygons at zoom >= 14

  // Track current rendering state to avoid unnecessary redraws
  private lastFieldsRenderMode: 'detailed' | 'clustered' | null = null;

  // Track markers and polygons added by this manager
  private managedMarkerIds: Set<string> = new Set();
  private managedPolygonIds: Set<string | number> = new Set();

  public setZoomThreshold(threshold: number): void {
    try {
      this.zoomThreshold = Number(threshold) || this.zoomThreshold;
      // Re-evaluate display after threshold change
      this.updateDisplay();
    } catch (err) {
      console.warn('Failed to set zoom threshold', err);
    }
  }

  /**
   * Initialize the manager with a map instance
   */
  initialize(mapInstance: OpenStreetMapComponent): void {
    this.mapInstance = mapInstance;
    this.setupZoomListener();

    // Subscribe to field changes
    this.fields$.subscribe(() => {
      this.updateDisplay();
    });
  }

  /**
   * Setup zoom listener to update rendering based on zoom level
   */
  private setupZoomListener(): void {
    if (this.mapInstance?.mapZoomChange$) {
      this.mapInstance.mapZoomChange$.subscribe((zoom: number) => {
        this.currentZoom = zoom;
        this.updateDisplay();
      });
    }
  }

  /**
   * Load fields from API
   */
  loadField(
    params: {
      west: number;
      east: number;
      south: number;
      north: number;
      query?: {};
    },
    center?: boolean,
  ): Observable<Field[]> {
    // Reset render mode when loading new fields
    this.lastFieldsRenderMode = null;

    const query = params.query;
    // @ts-ignore

    setTimeout(() => {
      if (this.mapInstance) {
        this.mapInstance.isLoading = true;
      }
    });
    return this.fieldService.cropMonitoringFieldGetFieldsByCoordinatesPost(params).pipe(
      map((fields: Field[]) => {
        let nameFilterValue: string | null = null;

        // @ts-ignore
        if (query && query['filters'] && Array.isArray(query['filters'])) {
          // @ts-ignore
          const nameFilter = query['filters'].find((filter: any) => filter.filterField === 'name');
          nameFilterValue = nameFilter?.filterValue || null;
        }

        this.fields$.next(fields);

        const nameFilterChanged = nameFilterValue !== this.lastNameFilterValue;

        this.lastNameFilterValue = nameFilterValue;

        this.fields$.next(fields);
        if (center || nameFilterChanged) {
          if (this.mapInstance) {
            this.mapInstance.isLoading = false;
          }
          this.centerOnFieldsOrDefaultZoom(fields);
        }
        return fields;
      }),
    );
  }

  /**
   * Update display based on current zoom
   */
  private updateDisplay(): void {
    if (!this.mapInstance) {
      return;
    }

    const fields = this.fields$.value;

    // Determine the current render mode based on zoom
    const currentMode: 'detailed' | 'clustered' = this.currentZoom >= this.zoomThreshold ? 'detailed' : 'clustered';

    // Only redraw if the mode has changed
    if (this.lastFieldsRenderMode === currentMode) {
      return; // Don't redraw if we're in the same mode
    }

    // Clear only the layers that need to change
    if (this.lastFieldsRenderMode === 'clustered') {
      // Switching from clustered to detailed: remove markers only
      const fieldMarkerIds = Array.from(this.managedMarkerIds).filter((id) => id.startsWith('field_marker_'));
      fieldMarkerIds.forEach((markerId) => {
        this.mapInstance!.mapController$.next({ removeMarker: { id: markerId } });
        this.managedMarkerIds.delete(markerId);
      });
    } else if (this.lastFieldsRenderMode === 'detailed') {
      // Switching from detailed to clustered: remove polygons only
      const currentFields = this.fields$.value;
      currentFields.forEach((field) => {
        if (field?.id && this.managedPolygonIds.has(field.id)) {
          this.mapInstance!.mapController$.next({ removePolygon: { id: field.id } });
          this.managedPolygonIds.delete(field.id);
        }
      });
    }

    // Update the last render mode
    this.lastFieldsRenderMode = currentMode;

    // Render the new mode
    if (currentMode === 'detailed') {
      // At high zoom: show polygons (detailed)
      this.renderDetailedView(fields);
    } else {
      // At low zoom: show markers (clustered)
      this.renderClusteredView(fields);
    }
  }

  /**
   * Render detailed view - show polygons
   */
  private renderDetailedView(fields: Field[]): void {
    if (!this.mapInstance) return;

    // Only render polygons, no markers
    fields.forEach((field) => {
      if (field?.polygonJson) {
        this.addFieldPolygon(field);
      }
    });
  }

  /**
   * Render clustered view - show markers for polygon centers at low zoom
   */
  private renderClusteredView(fields: Field[]): void {
    if (!this.mapInstance) return;

    // At low zoom, show markers representing field polygons
    const markers = fields
      .filter((field) => field?.polygonJson)
      .map((field) => this.createMarkerFromFieldPolygon(field))
      .filter((marker) => marker !== null);

    // Track marker IDs
    markers.forEach((marker) => this.managedMarkerIds.add(marker.id));

    this.mapInstance.mapController$.next({ addMarkers: markers });
  }

  /**
   * Create marker from field polygon (for clustered view)
   */
  private createMarkerFromFieldPolygon(field: Field): any {
    if (!field?.polygonJson) return null;

    // Calculate center of polygon for marker position
    const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(field.polygonJson);

    if (!coordinates || coordinates.length === 0) return null;

    // Calculate centroid
    let latSum = 0;
    let lngSum = 0;
    coordinates.forEach(([lat, lng]) => {
      latSum += lat;
      lngSum += lng;
    });
    const centerLat = latSum / coordinates.length;
    const centerLng = lngSum / coordinates.length;

    const marker = {
      id: `field_marker_${field.id}`,
      lat: centerLat,
      lng: centerLng,
      title: field.name || this.translate.instant('FIELD'),
      popup: `<b>${field.name || this.translate.instant('FIELD')}</b><br>
              <small>${this.translate.instant('CADASTRAL_NUMBER')}: ${field.name || 'N/A'}</small><br>
              <small>${this.translate.instant('AREA')}: ${field.fieldArea || 'N/A'} ha</small>`,
      data: {
        field,
      },
      // Provide HTML icon directly so leaflet addMarkers uses it
      icon: `<div class="field-marker-content"><i class="pi pi-map-marker"></i></div>`,
      withCluster: true,
      onClick: (m: any) => {
        try {
          const eventsController = this.mapInstance?.getEventsController?.();
          if (eventsController && typeof eventsController.emitMarkerClick === 'function') {
            eventsController.emitMarkerClick({
              id: marker.id,
              lat: marker.lat,
              lng: marker.lng,
              data: marker.data,
            });
          }
          // Also emit through local subject
          this.fieldMarkerClick$.next({
            lat: marker.lat,
            lng: marker.lng,
            data: marker.data,
          });
        } catch (err) {
          console.warn('Failed to emit field marker click', err);
        }
      },
    };

    return marker;
  }

  /**
   * Add a polygon for a field
   */
  private addFieldPolygon(field: Field): void {
    if (!this.mapInstance || !field?.polygonJson) return;

    // Check if polygon already exists to avoid duplicates
    if (field.id && this.managedPolygonIds.has(field.id)) {
      return;
    }

    const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(field.polygonJson);

    if (!PolygonConverterUtil.isValidPolygon(coordinates)) {
      return;
    }

    // Track polygon ID
    this.managedPolygonIds.add(field.id!);

    this.mapInstance.mapController$.next({
      addPolygon: {
        coordinates: coordinates,
        properties: {
          id: field.id,
          name: field?.name,
          strokeColor: '#ffffff',
          strokeWidth: 2,
          fillColor: 'rgba(156, 195, 26, 1)',
          fill: true,
          fillOpacity: 0.8,
          data: {
            field,
          },
          onClick: (polygon: any) => {
            this.onFieldPolygonClick(polygon);
          },
        },
      },
    });
  }

  /**
   * Handle field polygon click
   */
  private onFieldPolygonClick(polygon: any): void {
    this.fieldPolygonClick$.next(polygon);
  }

  /**
   * Clear only field-related markers and polygons
   */
  clearFieldLayers(): void {
    if (!this.mapInstance) return;

    // Get current fields before clearing state
    const currentFields = this.fields$.value;

    // Remove only field markers (those starting with 'field_marker_')
    const fieldMarkerIds = Array.from(this.managedMarkerIds).filter((id) => id.startsWith('field_marker_'));
    this.mapInstance!.mapController$.next({ removeMarkers: fieldMarkerIds });

    fieldMarkerIds.forEach((markerId) => {
      this.managedMarkerIds.delete(markerId);
    });

    // Remove field polygons - iterate through all tracked polygon IDs
    const polygonIdsToRemove = Array.from(this.managedPolygonIds);

    this.mapInstance!.mapController$.next({ removePolygons: polygonIdsToRemove as string[] });

    polygonIdsToRemove.forEach((polygonId) => {
      this.managedPolygonIds.delete(polygonId);
    });

    // Also try to remove polygons by field IDs (in case tracking was missed)
    currentFields.forEach((field) => {
      if (field?.id && !polygonIdsToRemove.includes(field.id)) {
        // This field polygon wasn't tracked, but try to remove it anyway
        this.mapInstance!.mapController$.next({ removePolygon: { id: field.id } });
      }
    });

    // Clear fields from state
    this.fields$.next([]);

    // Reset render mode
    this.lastFieldsRenderMode = null;
  }

  /**
   * Clear fields from state
   */
  clearFields(): void {
    this.fields$.next([]);
    this.clearFieldLayers();
  }

  getRegionsFieldCount(query?: {}): Observable<GisCircleMarker[]> {
    // Get region IDs from the region manager's cache
    let regionIds = this.regionManager.getCachedRegionIds();

    // Check if there's a regionId filter in the query
    // @ts-ignore
    if (query && query['filters'] && Array.isArray(query['filters'])) {
      // @ts-ignore
      const regionFilter = query['filters'].find((filter: any) => filter.filterField === 'regionId');

      if (regionFilter && regionFilter.filterValue) {
        regionIds = [regionFilter.filterValue];
      }
    }

    return this.fieldService
      .cropMonitoringFieldGetFieldsByRegionsPost({
        regionIds: regionIds,
        query: query,
      })
      .pipe(
        map((response) => {
          const fieldsByRegion = (response as unknown as FieldsByRegionsResponse).fields;

          // Check if only one region with count = 1
          const entries = Object.entries(fieldsByRegion);
          if (entries.length === 1 && entries[0][1] === 1) {
            // Load the single field - use bounding box from region or default
            const regionId = entries[0][0];
            const regionPolygon = this.regionManager.getRegionPolygonsCache().get(regionId);
            if (regionPolygon) {
              const bounds = this.calculateBoundsFromPolygon(regionPolygon?.polygon);

              const unSub = this.loadField(
                {
                  west: bounds.west,
                  east: bounds.east,
                  south: bounds.south,
                  north: bounds.north,
                  query: query,
                },
                true,
              ).subscribe(() => {
                if (this.mapInstance) {
                  this.mapInstance.isLoading = false;
                }
                unSub.unsubscribe();
              });
            }
            return [];
          } else {
            return createRegionCircleMarkers(
              fieldsByRegion,
              this.regionManager.getRegionPolygonsCache(),
              this.COLOR_FIELD_COUNT,
              this.mapInstance,
            );
          }
        }),
      );
  }

  private centerOnFieldsOrDefaultZoom(fields: Field[]): void {
    if (!this.mapInstance) return;
    if (fields.length === 1 && fields[0]?.polygonJson) {
      const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(fields[0].polygonJson);
      if (coordinates && coordinates.length > 0) {
        // Calculate centroid
        let latSum = 0;
        let lngSum = 0;
        coordinates.forEach(([lat, lng]) => {
          latSum += lat;
          lngSum += lng;
        });
        const centerLat = latSum / coordinates.length;
        const centerLng = lngSum / coordinates.length;

        this.mapInstance.mapController$.next({
          setCenter: { lat: centerLat, lng: centerLng, zoom: 16 },
        });
      }
    }
  }

  private calculateBoundsFromPolygon(polygon: any): { west: number; east: number; south: number; north: number } {
    const coordinates: any[] = polygon?.coordinates;
    let west = Infinity,
      east = -Infinity,
      south = Infinity,
      north = -Infinity;

    coordinates?.forEach(([lat, lng]) => {
      if (lng < west) west = lng;
      if (lng > east) east = lng;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
    });

    return { west, east, south, north };
  }
}
